import { useEffect, useRef, useState, type ElementType, type ReactNode } from 'react';
import { Check, Copy, Globe, Mail, MapPin, Phone, Smartphone, Star } from 'lucide-react';
import { cn } from '../../lib/cn';
import { mailHref, webHref } from '../../lib/links';
import { telHref } from '../../lib/tel';
import { Button } from '../ui/button';
import { Icon, type LucideIcon } from '../ui/icon';

export interface KontaktProps {
  /** Name der Person. Fehlt bei den Kontaktdaten einer Firma. */
  name?: ReactNode;
  /** Detailseite der Person; der Name wird dann ein Link. */
  href?: string;
  /** Link-Komponente für `href`, z. B. ein Adapter auf react-router. Standard: <a>. */
  linkAs?: ElementType;
  /** Position oder Rolle, unter dem Namen. */
  position?: ReactNode;
  /** Stern hinter dem Namen: der primäre Ansprechpartner. */
  primaer?: boolean;
  telefon?: string | null;
  mobil?: string | null;
  email?: string | null;
  /** Nur `kacheln`: Kachel, die die Website in einem neuen Tab öffnet. */
  website?: string | null;
  /** Nur `kacheln`: Anschrift als Text, Zeilen als Kinder. */
  adresse?: ReactNode;
  /**
   * `kacheln` (Standard): die Kontaktdaten einer Seite, je Weg eine Kachel mit
   * Bezeichnung und Wert. Am Rechner kopiert ein Klick den Wert, am Handy ist
   * die ganze Kachel ein Link (`tel:`, `mailto:`). Eine Mailadresse, die keine
   * schlichte Adresse ist, und eine Website ohne `http:`/`https:` stehen nur
   * als Text da (`lib/links.ts`).
   * `knoepfe`: ein Ansprechpartner. Am Handy Knöpfe „Anrufen“, „Mobil
   * anrufen“, „E-Mail schreiben“; am Rechner die Werte in einer Zeile, ein
   * Klick kopiert.
   */
  darstellung?: 'kacheln' | 'knoepfe';
  className?: string;
}

interface Weg {
  key: string;
  /** `null`: kein Link, der Wert steht am Handy nur als Text da. */
  href: string | null;
  wert: string;
  label: string;
  /** Text des Knopfs am Handy, Verb zuerst. */
  aktion: string;
  icon: LucideIcon;
}

/** Kacheln wie bisher im CRM: E-Mail, Telefon, Mobil. Knöpfe: Anrufen zuerst, das ist am Handy der häufigste Weg. */
function wegeAus({ telefon, mobil, email, darstellung }: Pick<KontaktProps, 'telefon' | 'mobil' | 'email' | 'darstellung'>): Weg[] {
  const mail: Weg[] = email ? [{ key: 'mail', href: mailHref(email), wert: email, label: 'E-Mail', aktion: 'E-Mail schreiben', icon: Mail }] : [];
  const tel: Weg[] = [];
  if (telefon) tel.push({ key: 'tel', href: telHref(telefon), wert: telefon, label: 'Telefon', aktion: 'Anrufen', icon: Phone });
  if (mobil) tel.push({ key: 'mobil', href: telHref(mobil), wert: mobil, label: 'Mobil', aktion: telefon ? 'Mobil anrufen' : 'Anrufen', icon: Smartphone });
  return darstellung === 'knoepfe' ? [...tel, ...mail] : [...mail, ...tel];
}

/** Am Rechner: Ein Klick legt den Wert in die Zwischenablage, ein Haken bestätigt. */
function Kopieren({ wert, children, className }: { wert: string; children?: ReactNode; className?: string }) {
  const [kopiert, setKopiert] = useState(false);
  const zeit = useRef<number>();
  useEffect(() => () => window.clearTimeout(zeit.current), []);
  return (
    <button
      type="button"
      title="In die Zwischenablage kopieren"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(wert);
        } catch {
          return;
        }
        setKopiert(true);
        window.clearTimeout(zeit.current);
        zeit.current = window.setTimeout(() => setKopiert(false), 2000);
      }}
      className={cn('group inline-flex max-w-full cursor-pointer items-center gap-1.5 rounded-sm text-left transition-colors hover:text-highlight', className)}
    >
      {children ?? <span className="truncate">{wert}</span>}
      {kopiert ? (
        <Icon icon={Check} size={14} className="text-success" label="Kopiert" />
      ) : (
        <Icon icon={Copy} size={14} className="opacity-0 transition-opacity group-hover:opacity-60 group-focus-visible:opacity-60" />
      )}
    </button>
  );
}

const kachel = 'flex min-w-0 items-center gap-3 rounded-lg bg-muted p-3';

function Kopf({ name, href, linkAs: LinkAs = 'a', position, primaer }: Pick<KontaktProps, 'name' | 'href' | 'linkAs' | 'position' | 'primaer'>) {
  if (!name) return null;
  const inhalt = (
    <>
      <span className="flex min-w-0 items-center gap-1.5">
        <span className={cn('min-w-0 truncate t-body-strong', href && 'text-link group-hover:underline')}>{name}</span>
        {primaer && <Icon icon={Star} size={13} className="text-highlight" label="Primär" />}
      </span>
      {position && <span className="block truncate t-small text-muted-foreground">{position}</span>}
    </>
  );
  // Mit Link ist der ganze Kopf das Ziel, auf Touch mindestens 44 px hoch.
  return href ? (
    <LinkAs href={href} className="group flex min-w-0 flex-col justify-center self-start no-underline touch:min-h-11">
      {inhalt}
    </LinkAs>
  ) : (
    <div className="min-w-0">{inhalt}</div>
  );
}

/**
 * Wie man jemanden erreicht: Name und Position, Telefon, Mobil, E-Mail. Am
 * Rechner zum Kopieren, am Handy zum Antippen (README „Mobil“). Umgeschaltet
 * per CSS unter 768 px, nicht per JavaScript.
 */
export function Kontakt({ name, href, linkAs, position, primaer, telefon, mobil, email, website, adresse, darstellung = 'kacheln', className }: KontaktProps) {
  const wege = wegeAus({ telefon, mobil, email, darstellung });
  const webLink = website ? webHref(website) : null;
  const leer = wege.length === 0 && (darstellung === 'knoepfe' || (!website && !adresse));
  if (leer && !name) return null;
  const kopf = <Kopf name={name} href={href} linkAs={linkAs} position={position} primaer={primaer} />;

  if (darstellung === 'knoepfe') {
    return (
      <div className={cn('flex min-w-0 flex-col gap-2', className)}>
        {kopf}
        {wege.length > 0 && (
          <>
            <div className="flex flex-wrap gap-x-4 gap-y-1 t-small text-muted-foreground max-md:hidden">
              {wege.map((w) => (
                <Kopieren key={w.key} wert={w.wert}>
                  <Icon icon={w.icon} />
                  <span className="truncate">{w.wert}</span>
                </Kopieren>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 md:hidden">
              {wege.map((w) =>
                w.href ? (
                  <Button key={w.key} asChild icon={w.icon}>
                    <a href={w.href}>{w.aktion}</a>
                  </Button>
                ) : (
                  <span key={w.key} className="flex min-w-0 basis-full items-center gap-1.5 t-small text-muted-foreground">
                    <Icon icon={w.icon} />
                    <span className="truncate">{w.wert}</span>
                  </span>
                ),
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={cn('@container flex min-w-0 flex-col gap-3', className)}>
      {kopf}
      {!leer && (
        <div className="grid grid-cols-1 gap-3 @md:grid-cols-2">
          {wege.map((w) => (
            <div key={w.key} className="contents">
              <div className={cn(kachel, 'max-md:hidden')}>
                <Icon icon={w.icon} size={18} className="text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="t-label text-muted-foreground">{w.label}</div>
                  <Kopieren wert={w.wert} className={cn('w-full', w.key === 'mail' && 'text-link')} />
                </div>
              </div>
              {w.href ? (
                <a href={w.href} className={cn(kachel, 'no-underline transition-colors hover:bg-accent md:hidden')}>
                  <Icon icon={w.icon} size={18} className="text-highlight" />
                  <div className="min-w-0 flex-1">
                    <div className="t-label text-muted-foreground">{w.label}</div>
                    <div className="truncate text-link">{w.wert}</div>
                  </div>
                </a>
              ) : (
                <div className={cn(kachel, 'md:hidden')}>
                  <Icon icon={w.icon} size={18} className="text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="t-label text-muted-foreground">{w.label}</div>
                    <div className="truncate">{w.wert}</div>
                  </div>
                </div>
              )}
            </div>
          ))}
          {website &&
            (webLink ? (
              <a href={webLink} target="_blank" rel="noopener noreferrer" className={cn(kachel, 'no-underline transition-colors hover:bg-accent')}>
                <Icon icon={Globe} size={18} className="text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="t-label text-muted-foreground">Website</div>
                  <div className="truncate text-link">{website}</div>
                </div>
              </a>
            ) : (
              <div className={kachel}>
                <Icon icon={Globe} size={18} className="text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="t-label text-muted-foreground">Website</div>
                  <div className="truncate">{website}</div>
                </div>
              </div>
            ))}
          {adresse && (
            <div className={kachel}>
              <Icon icon={MapPin} size={18} className="text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="t-label text-muted-foreground">Adresse</div>
                <div>{adresse}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
